'use client'

import { Box, CirclePlay, Columns2, Hammer, Images, Plus, Ruler, type LucideIcon } from 'lucide-react'
import { motion, useReducedMotion, type Transition } from 'motion/react'
import React, { useRef } from 'react'

import { cn } from '@/utilities/ui'

/**
 * Polished, adaptive project tab bar — Motion-driven.
 *
 * - 3–6 tabs always stay on ONE row (desktop/tablet) via CSS grid
 *   `repeat(var(--tab-count), minmax(0, 1fr))`.
 * - Label/icon sizing clamps by tab count: 3 = large, 4 = normal, 5–6 = compact.
 * - Mobile falls back to a snap horizontal scroll.
 * - The active "bubble" is a shared `layoutId` element that springs/morphs from
 *   one tab to the next. Hover lift and pointer press use Motion spring physics.
 *   A symmetric tiered resting offset, an animated gradient sheen, icon
 *   rotate-on-hover, and the focus ring stay in CSS (cheap, GPU-composited).
 *
 * Tabs stay real `role="tab"` buttons (they switch panels, they don't navigate),
 * with roving tabindex + arrow-key navigation per the WAI-ARIA tabs pattern.
 * All motion respects `prefers-reduced-motion`.
 */

export type ProjectTabKind = 'photoGallery' | 'embed' | 'videos' | 'cta' | 'floorplan'

export type ProjectTab = {
  id: string
  label: string
  tabKind?: ProjectTabKind
}

/** Map a tab to its Lucide icon. Keyed off the reliable `tabKind`, with a
 *  before/after label override (galleries are sometimes split that way). */
function iconFor(tab: ProjectTab): LucideIcon {
  if (/\bbefore\b|after|b\s*&\s*a/i.test(tab.label)) return Columns2
  switch (tab.tabKind) {
    case 'photoGallery':
      return Images
    case 'videos':
      return CirclePlay
    case 'embed':
      return Box // 3D tour
    case 'floorplan':
      return Ruler
    case 'cta':
      return Hammer
    default:
      return Plus
  }
}

type SizeKey = 'lg' | 'md' | 'sm'

/** 3 tabs → large, 4 → normal, 5–6 → compact. */
function sizeForCount(count: number): SizeKey {
  if (count <= 3) return 'lg'
  if (count === 4) return 'md'
  return 'sm'
}

const SIZE: Record<SizeKey, { btn: string; icon: string }> = {
  lg: { btn: 'gap-2.5 px-[26px] py-[15px] text-[15px]', icon: 'h-[18px] w-[18px]' },
  md: { btn: 'gap-2 px-5 py-[14px] text-[14px]', icon: 'h-4 w-4' },
  sm: { btn: 'gap-1.5 px-3 py-[13px] text-[13px] tracking-tight', icon: 'h-[14px] w-[14px]' },
}

const BUBBLE_SPRING: Transition = { type: 'spring', stiffness: 420, damping: 34 }
const LIFT_SPRING: Transition = { type: 'spring', stiffness: 500, damping: 30, mass: 0.6 }

type ButtonProps = {
  tab: ProjectTab
  isActive: boolean
  index: number
  count: number
  size: SizeKey
  onSelect: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  tabId: string
  panelId: string
  buttonRef: (node: HTMLButtonElement | null) => void
}

export function ProjectTabButton({
  tab,
  isActive,
  index,
  count,
  size,
  onSelect,
  onKeyDown,
  tabId,
  panelId,
  buttonRef,
}: ButtonProps) {
  const reduce = useReducedMotion()
  const Icon = iconFor(tab)
  const s = SIZE[size]

  // Symmetric tier: center tabs rest highest, edges lowest — a balanced
  // "row of physical keys" stagger for any count. Active sits flat & raised.
  const center = (count - 1) / 2
  const restY = reduce ? 0 : isActive ? -2 : Math.round(Math.abs(index - center) * 1.5)

  return (
    <motion.button
      ref={buttonRef}
      type="button"
      role="tab"
      id={tabId}
      aria-selected={isActive}
      aria-controls={panelId}
      tabIndex={isActive ? 0 : -1}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      // Resting tier; Motion spring physics for hover lift + pointer press.
      animate={{ y: restY }}
      whileHover={reduce ? undefined : { scale: 1.015 }}
      whileTap={reduce ? undefined : { scale: 0.98 }}
      transition={LIFT_SPRING}
      className={cn(
        'group relative isolate flex min-h-[48px] min-w-[44%] shrink-0 snap-start items-center justify-center overflow-hidden rounded-[3px] text-center font-semibold whitespace-nowrap select-none',
        'sm:min-w-[150px] md:min-w-0',
        s.btn,
        // Focus ring.
        'focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1595a7] focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        isActive
          ? 'cursor-default text-white'
          : 'cursor-pointer text-[#1d1d1b] shadow-[0_2px_0_rgba(0,0,0,0.10)] hover:text-[#009BAB] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)]',
      )}
    >
      {/* Static cream base fill for inactive tabs. */}
      <span
        aria-hidden
        className={cn(
          'absolute inset-0 -z-20 transition-colors duration-300',
          isActive ? 'bg-transparent' : 'bg-[#F7E8B1] group-hover:bg-[#e9f4f1]',
        )}
      />

      {/* Active "bubble": a single shared element that springs between tabs. */}
      {isActive && (
        <motion.span
          aria-hidden
          layoutId="project-tab-bubble"
          transition={reduce ? { duration: 0 } : BUBBLE_SPRING}
          className="absolute inset-0 -z-10 overflow-hidden rounded-[3px] bg-[#72c7d0] shadow-[0_8px_22px_rgba(21,149,167,0.45)]"
        >
          {/* Continuous sheen that travels with the bubble. */}
          <span className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-0 animate-[tab-sheen_3.4s_ease-in-out_infinite] motion-reduce:animate-none" />
        </motion.span>
      )}

      {/* Single sheen sweep on hover for inactive tabs. */}
      {!isActive && (
        <span aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <span className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-0 group-hover:animate-[tab-sheen_0.9s_ease-out] motion-reduce:animate-none" />
        </span>
      )}

      <span className="min-w-0 leading-tight">{tab.label}</span>

      <span
        aria-hidden
        className={cn(
          'inline-flex shrink-0 items-center justify-center transition-transform duration-300 ease-out',
          'group-hover:-rotate-6 group-hover:scale-110 motion-reduce:transform-none',
          isActive && 'scale-105',
        )}
      >
        {/* eslint-disable-next-line react-hooks/static-components -- iconFor returns statically-imported lucide components (stable identities); nothing is created during render */}
        <Icon
          className={cn(s.icon, isActive ? 'text-white' : 'text-[#1d1d1b] group-hover:text-[#009BAB]')}
          strokeWidth={2.25}
          aria-hidden
        />
      </span>
    </motion.button>
  )
}

type ListProps = {
  tabs: ProjectTab[]
  activeIndex: number
  onChange: (index: number) => void
  getTabId: (tab: ProjectTab) => string
  getPanelId: (tab: ProjectTab) => string
  ariaLabel: string
  className?: string
}

/** Adaptive one-row tablist. Owns the grid layout, size clamping, and roving
 *  arrow-key navigation; renders a {@link ProjectTabButton} per tab. */
export function ProjectTabList({
  tabs,
  activeIndex,
  onChange,
  getTabId,
  getPanelId,
  ariaLabel,
  className,
}: ListProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const size = sizeForCount(tabs.length)

  function focusTab(index: number) {
    const next = (index + tabs.length) % tabs.length
    onChange(next)
    refs.current[next]?.focus()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        focusTab(index + 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        focusTab(index - 1)
        break
      case 'Home':
        e.preventDefault()
        focusTab(0)
        break
      case 'End':
        e.preventDefault()
        focusTab(tabs.length - 1)
        break
    }
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{ ['--tab-count' as string]: tabs.length }}
      className={cn(
        // Mobile: snap horizontal scroll. pt-2 leaves room for hover lift / tier.
        'flex w-full flex-nowrap gap-2 overflow-x-auto overflow-y-visible px-1 pt-2 pb-1 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        // Desktop/tablet: one row, equal columns that shrink, never wrap.
        'md:grid md:gap-2 md:overflow-visible md:px-4 md:[grid-template-columns:repeat(var(--tab-count),minmax(0,1fr))] lg:px-6',
        className,
      )}
    >
      {tabs.map((tab, index) => (
        <ProjectTabButton
          key={tab.id}
          tab={tab}
          index={index}
          count={tabs.length}
          size={size}
          isActive={index === activeIndex}
          onSelect={() => onChange(index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          tabId={getTabId(tab)}
          panelId={getPanelId(tab)}
          buttonRef={(node) => {
            refs.current[index] = node
          }}
        />
      ))}
    </div>
  )
}
